import { ref, computed, onUnmounted, type ComputedRef } from 'vue'
import type { UnrecognizedPart, AiSearchTarget, AiSearchOutcome } from '@/types/ai-search.types'
import { AiSearchService } from '@/infrastructure/api/ai-search.service'
import { aiFiltersUnderstood, mapAiFiltersToSearchFilters } from '@/utils/ai-filter-mapping'

const RESPONSE_TYPE_LIST_BUILDER = 'webapp_filters_list_builder'
const RESPONSE_TYPE_PEOPLE_SEARCH = 'webapp_filters_people_search'

// Web Speech API types — not available in all TS environments
interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  onresult: ((event: { results: { [index: number]: { [index: number]: { transcript: string } } } }) => void) | null
  onend: (() => void) | null
  start(): void
  abort(): void
}

// AI searches in flight in any component. The top bar hands a query to the search view
// (`?aiq=`), which asks the AI, so the top bar reads this to show its search as busy.
const runningSearches = ref(0)

/** Whether an AI search is being worked out anywhere (Vue 2 shows a spinner in the AI box). */
export function useAiSearchRunning(): ComputedRef<boolean> {
  return computed(() => runningSearches.value > 0)
}

export function useAiSearch() {
  const service = new AiSearchService()

  const aiQuery = ref('')
  const loading = ref(false)
  const isListening = ref(false)
  // Speech recognition's own message (unsupported browser). The search messages are shown by
  // the view that applies the result (AiSearchFeedbackDialog).
  const errorDialog = ref({ show: false, title: '', text: '' })

  // Typing animation state
  const placeholderText = ref('')
  const fullPlaceholders = [
    'Show me companies in the UK with turnover over 1M',
    'List all companies in US in retail industry that has ecommerce and uses PayPal as payment method',
    'asos.com',
    '',
  ]
  let currentPlaceholderIndex = 0
  let charIndex = 0
  let typingInterval: ReturnType<typeof setInterval> | null = null
  let recognition: SpeechRecognitionLike | null = null
  let _onVoiceResult: ((transcript: string) => void) | null = null

  // Classifies + maps an AI query into an AiSearchOutcome. Writes to NO store and
  // performs NO navigation — the caller (a view or the top bar) applies the patch to
  // its own stores or hands off to the other view based on `outcome.kind`. `target`
  // seeds the request `type` the AI API receives; `response_type` is the authority.
  async function performAiSearch(
    query: string,
    username: string,
    target: AiSearchTarget = 'listbuilder',
  ): Promise<AiSearchOutcome> {
    if (!query.trim()) return { kind: 'general' }
    loading.value = true
    runningSearches.value++
    try {
      // URL detection — bypass AI API (matches onCompanySearch heuristic). The caller
      // applies this via its own onCompanySearch so per-view URL conventions are kept.
      const isUrl = query.includes('.') && !query.includes(' ')
      if (isUrl) {
        return { kind: 'url', website: query }
      }

      const response = await service.queryAiSearch(query, target, username)
      const responseType = response.response_type?.trim().toLowerCase()

      if (
        (responseType === RESPONSE_TYPE_LIST_BUILDER ||
          responseType === RESPONSE_TYPE_PEOPLE_SEARCH) &&
        Array.isArray(response.webapp_search_filters) &&
        response.webapp_search_filters.length > 0
      ) {
        const filters = response.webapp_search_filters[0]

        // The parts it could not interpret; the caller shows them before applying (Vue 2).
        const ignored = Array.isArray(filters.unrecognized_parts_of_query)
          ? (filters.unrecognized_parts_of_query as UnrecognizedPart[])
          : []
        if (!aiFiltersUnderstood(filters)) return { kind: 'nothing', ignored }

        const patch = await mapAiFiltersToSearchFilters(filters, service)
        const kind: AiSearchTarget =
          responseType === RESPONSE_TYPE_PEOPLE_SEARCH ? 'peoplesearch' : 'listbuilder'
        return { kind, patch, ignored }
      }

      return { kind: 'general' }
    } catch {
      return { kind: 'error' }
    } finally {
      loading.value = false
      runningSearches.value--
    }
  }

  // Voice recognition
  function startListening(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any
    const SpeechRecognitionCtor = win.SpeechRecognition || win.webkitSpeechRecognition
    if (!SpeechRecognitionCtor) {
      errorDialog.value = {
        show: true,
        title: 'Not supported',
        text: 'Your browser does not support Speech Recognition.',
      }
      return
    }
    if (!recognition) {
      const rec: SpeechRecognitionLike = new SpeechRecognitionCtor()
      rec.lang = 'en-US'
      rec.interimResults = false
      rec.maxAlternatives = 1
      rec.onresult = (event) => {
        const transcript = event.results[0][0].transcript
        aiQuery.value = transcript
        if (_onVoiceResult) _onVoiceResult(transcript)
      }
      rec.onend = () => {
        isListening.value = false
      }
      recognition = rec
    }
    isListening.value = true
    recognition.start()
  }

  function stopListening(): void {
    if (recognition && isListening.value) {
      recognition.abort()
      isListening.value = false
    }
  }

  // Typing animation
  function startTypingEffect(): void {
    if (typingInterval) return
    const typeNextChar = () => {
      const currentText = fullPlaceholders[currentPlaceholderIndex]
      if (charIndex < currentText.length) {
        placeholderText.value += currentText[charIndex]
        charIndex++
      } else {
        clearInterval(typingInterval!)
        typingInterval = null
        if (currentPlaceholderIndex === fullPlaceholders.length - 1) {
          placeholderText.value = 'AI Search - What are you looking for?'
        } else {
          setTimeout(() => {
            charIndex = 0
            placeholderText.value = ''
            currentPlaceholderIndex++
            startTypingEffect()
          }, 2000)
        }
      }
    }
    typingInterval = setInterval(typeNextChar, 70)
  }

  function dismissError(): void {
    errorDialog.value = { show: false, title: '', text: '' }
  }

  onUnmounted(() => {
    if (typingInterval) {
      clearInterval(typingInterval)
      typingInterval = null
    }
    stopListening()
  })

  return {
    aiQuery,
    loading,
    isListening,
    errorDialog,
    placeholderText,
    performAiSearch,
    startListening,
    stopListening,
    startTypingEffect,
    dismissError,
    setOnVoiceResult: (cb: ((transcript: string) => void) | null) => { _onVoiceResult = cb },
  }
}
